import { FC, useEffect, useRef, useState } from 'react';
import {
  CustomDropdownArrow,
  CustomDropdownButton,
  CustomDropdownContainer,
  CustomDropdownContent,
  CustomDropdownIcon,
  CustomDropdownList,
  CustomDropdownListDown,
  CustomDropdownOption,
  CustomDropdownOptionCheck
} from '../styles';

interface DropdownOption {
  value: string;
  label: string;
  icon?: string;
  proofResult?: string;
}

interface CustomDropdownProps {
  value: string;
  onChange: (value: string) => void;
  options: DropdownOption[];
  placeholder?: string;
  disabled?: boolean;
  width?: string;
  direction?: 'up' | 'down';
  id?: string;
}

const CustomDropdown: FC<CustomDropdownProps> = ({
  value,
  onChange,
  options,
  placeholder = 'Select...',
  disabled = false,
  width,
  direction = 'up',
  id
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleOutsideInteraction = (event: Event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    const handleWindowBlur = () => {
      setIsOpen(false);
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleOutsideInteraction, true);
      document.addEventListener('click', handleOutsideInteraction, true);
      document.addEventListener('touchstart', handleOutsideInteraction, true);
      window.addEventListener('blur', handleWindowBlur);
      window.addEventListener('focus', handleOutsideInteraction, true);
    }

    return () => {
      document.removeEventListener('mousedown', handleOutsideInteraction, true);
      document.removeEventListener('click', handleOutsideInteraction, true);
      document.removeEventListener('touchstart', handleOutsideInteraction, true);
      window.removeEventListener('blur', handleWindowBlur);
      window.removeEventListener('focus', handleOutsideInteraction, true);
    };
  }, [isOpen]);

  const selectedOption = options.find((opt) => opt.value === value);

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (disabled) return;

    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      setIsOpen(!isOpen);
    } else if (event.key === 'Escape') {
      setIsOpen(false);
    }
  };

  const handleOptionKeyDown = (event: React.KeyboardEvent, optionValue: string) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onChange(optionValue);
      setIsOpen(false);
    }
  };

  const getDisplayContent = () => {
    if (!selectedOption) {
      return <span>{placeholder}</span>;
    }
    return (
      <CustomDropdownContent>
        <span>{selectedOption.label}</span>
        {selectedOption.icon && (
          <CustomDropdownIcon src={selectedOption.icon} alt={selectedOption.label} />
        )}
      </CustomDropdownContent>
    );
  };

  const DropdownListComponent = direction === 'down' ? CustomDropdownListDown : CustomDropdownList;

  return (
    <CustomDropdownContainer ref={dropdownRef} style={width ? { width } : {}}>
      <CustomDropdownButton
        id={id}
        data-testid={id}
        role="button"
        tabIndex={disabled ? -1 : 0}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        title={selectedOption?.label}
        onClick={() => !disabled && setIsOpen(!isOpen)}
        onKeyDown={handleKeyDown}
        isOpen={isOpen}
        style={{
          cursor: disabled ? 'not-allowed' : 'pointer',
          opacity: disabled ? 0.6 : 1
        }}
      >
        {getDisplayContent()}
        <CustomDropdownArrow src="/assets/images/dropdown.svg" alt="dropdown" isOpen={isOpen} />
      </CustomDropdownButton>

      {isOpen && !disabled && (
        <DropdownListComponent role="listbox">
          {options.map((option) => (
            <CustomDropdownOption
              key={option.value}
              role="option"
              tabIndex={0}
              aria-selected={value === option.value}
              data-proof-result={option.proofResult}
              data-testid={`doc-option-${option.value}`}
              onClick={() => {
                onChange(option.value);
                setIsOpen(false);
              }}
              onKeyDown={(e) => handleOptionKeyDown(e, option.value)}
              selected={value === option.value}
            >
              <span>{option.label}</span>
              {option.icon && <CustomDropdownIcon src={option.icon} alt={option.label} />}
              {value === option.value ? (
                <CustomDropdownOptionCheck
                  src="/assets/images/dropdown-selected.svg"
                  alt="selected"
                />
              ) : null}
            </CustomDropdownOption>
          ))}
        </DropdownListComponent>
      )}
    </CustomDropdownContainer>
  );
};

export default CustomDropdown;
